import { useMemo, useState } from "react";
import { useRoles } from "newui/features/RolesPermissions/hooks/useUserAssignments";
import type { UserAssignment } from "newui/features/RolesPermissions/types/RolesPermissions.type";
import { ScopeLabel } from "newui/features/RolesPermissions/components/ScopeLabel";

interface RbacRolePillsProps {
	assignment: UserAssignment | null;
	/** How many role pills to show before collapsing the rest behind "+N more". */
	maxVisible?: number;
}

/** Enough to tell at a glance what someone is, without paging the row. */
const DEFAULT_MAX_VISIBLE = 2;

/**
 * Renders a user's RBAC v2 roles as pills (the union of their assignments).
 *
 * AC5: multi-role users show the union of their roles; effective per-module level
 * is server-authoritative (EffectivePermissionsResource). This never client-computes
 * a most-restrictive intersection — it lists the assigned roles. Shared between the
 * account users table and any RBAC-aware row.
 *
 * PRO-732c: the "hybrid" badge is NOT "more than one role" — it only means the
 * user holds roles on BOTH sides (procurer and bidder). A bidder-only company's
 * user with two bidder-side roles is not hybrid. `UserRoleAssignment` carries no
 * `side` of its own, so this joins each assignment's `role_id` to the cached
 * roles list (useRoles, react-query) to resolve it. Calling the hook here (rather
 * than threading a side lookup down as a prop) keeps both call sites (UserListRow,
 * UserDrawer) unchanged.
 */
export const RbacRolePills = ({
	assignment,
	maxVisible = DEFAULT_MAX_VISIBLE,
}: RbacRolePillsProps) => {
	const { roles } = useRoles();
	const [isExpanded, setIsExpanded] = useState(false);
	const roleSideById = useMemo(() => {
		const map = new Map<number, string | null | undefined>();
		roles.forEach((role) => map.set(role.id, role.side));
		return map;
	}, [roles]);

	const assignedRoles = assignment?.assignments ?? [];

	if (assignedRoles.length === 0) {
		return <span className="text-xs text-gray-400">No roles</span>;
	}

	// Roles still loading or a cloned/unknown role_id resolve to `undefined` here
	// and are simply not counted towards either side — degrade to no badge rather
	// than guessing.
	const hasProcurerRole = assignedRoles.some(
		(role) => roleSideById.get(role.role_id) === "procurer"
	);
	const hasBidderRole = assignedRoles.some(
		(role) => roleSideById.get(role.role_id) === "bidder"
	);
	const isHybrid = hasProcurerRole && hasBidderRole;

	/*
	 * PRO-731: a user can hold a role once per scope, so five specific RFx is five
	 * pills — and in the edit drawer that pushed Save and Cancel off the bottom,
	 * with the scroll falling through to the page behind. Showing every pill was
	 * never the point: two says what someone is, and the rest are one click away.
	 */
	const visibleRoles = isExpanded ? assignedRoles : assignedRoles.slice(0, maxVisible);
	const hiddenCount = assignedRoles.length - visibleRoles.length;

	return (
		<div className="flex flex-wrap items-center gap-1.5">
			{visibleRoles.map((role) => (
				<span
					key={role.assignment_id}
					className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700"
				>
					{role.role_name}
					{role.scope_label && (
						<>
							{" · "}
							{/* PRO-731 2.7: scope names are free text and blow out the pill. */}
							<ScopeLabel label={role.scope_label} />
						</>
					)}
				</span>
			))}
			{hiddenCount > 0 && (
				<button
					type="button"
					onClick={() => setIsExpanded(true)}
					className="rounded-full border border-gray-300 px-2 py-0.5 text-xs font-medium text-gray-600 hover:bg-gray-100"
					data-testid={`role-pills-more-${assignment?.id}`}
				>
					{`+${hiddenCount} more`}
				</button>
			)}
			{isExpanded && assignedRoles.length > maxVisible && (
				<button
					type="button"
					onClick={() => setIsExpanded(false)}
					className="rounded-full border border-gray-300 px-2 py-0.5 text-xs font-medium text-gray-600 hover:bg-gray-100"
					data-testid={`role-pills-less-${assignment?.id}`}
				>
					Show less
				</button>
			)}
			{isHybrid && (
				<span
					className="rounded bg-blue-900 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-white"
					title="Hybrid — holds both procurer and bidder roles; effective access is the union (most-permissive)"
					data-testid={`hybrid-badge-${assignment?.id}`}
				>
					hybrid
				</span>
			)}
		</div>
	);
};
